import React, { useEffect, useState } from 'react';
import { Header } from '@/components/layout/Header';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Progress } from '@/components/ui/progress';
import { vulnerabilitiesAPI, nvdAPI, reportsAPI } from '@/lib/api';
import { cn, getSeverityColor, formatDate } from '@/lib/utils';
import { 
  Bug, ExternalLink, Shield, AlertTriangle, Search, RefreshCw,
  CheckCircle, Copy, Server, Target, Lightbulb, FileText, Calendar, Download, CloudDownload
} from 'lucide-react';
import { toast } from 'sonner';

// CVE Details database
const getCVEDetails = (cve_id) => {
  const details = {
    'CVE-2024-3400': {
      vendor: 'Palo Alto Networks',
      product: 'PAN-OS GlobalProtect',
      affected_versions: '10.2.x < 10.2.9-h1, 11.0.x < 11.0.4-h1, 11.1.x < 11.1.2-h3',
      attack_vector: 'Network',
      attack_complexity: 'Low',
      privileges_required: 'None',
      user_interaction: 'None',
      patch_available: true,
      patch_version: '10.2.9-h1, 11.0.4-h1, 11.1.2-h3',
      workaround: 'Disable device telemetry feature until patch is applied.',
      exploit_available: true,
      actively_exploited: true,
      references: [
        { title: 'Palo Alto Security Advisory', url: 'https://security.paloaltonetworks.com/CVE-2024-3400' },
        { title: 'NVD Entry', url: 'https://nvd.nist.gov/vuln/detail/CVE-2024-3400' },
        { title: 'CISA Alert', url: 'https://www.cisa.gov/known-exploited-vulnerabilities-catalog' }
      ]
    },
    'CVE-2024-21762': {
      vendor: 'Fortinet',
      product: 'FortiOS SSL VPN',
      affected_versions: '6.0.x, 6.2.x, 6.4.x < 6.4.15, 7.0.x < 7.0.14, 7.2.x < 7.2.7, 7.4.x < 7.4.3',
      attack_vector: 'Network',
      attack_complexity: 'Low',
      privileges_required: 'None',
      user_interaction: 'None',
      patch_available: true,
      patch_version: '6.4.15, 7.0.14, 7.2.7, 7.4.3',
      workaround: 'Disable SSL VPN if not critical. Restrict access by source IP.',
      exploit_available: true,
      actively_exploited: true,
      references: [
        { title: 'FortiGuard Advisory', url: 'https://www.fortiguard.com/psirt/FG-IR-24-015' },
        { title: 'NVD Entry', url: 'https://nvd.nist.gov/vuln/detail/CVE-2024-21762' }
      ]
    },
    'CVE-2023-4966': {
      vendor: 'Citrix',
      product: 'NetScaler ADC / Gateway',
      affected_versions: '13.0.x < 13.0-92.19, 13.1.x < 13.1-49.15, 14.1.x < 14.1-8.50',
      attack_vector: 'Network',
      attack_complexity: 'Low',
      privileges_required: 'None',
      user_interaction: 'None',
      patch_available: true,
      patch_version: '13.0-92.19, 13.1-49.15, 14.1-8.50',
      workaround: 'Kill all active sessions after patching. Rotate credentials.',
      exploit_available: true,
      actively_exploited: true,
      references: [
        { title: 'Citrix Advisory', url: 'https://support.citrix.com/article/CTX579459' },
        { title: 'NVD Entry', url: 'https://nvd.nist.gov/vuln/detail/CVE-2023-4966' }
      ]
    },
    'CVE-2024-0012': {
      vendor: 'Palo Alto Networks',
      product: 'PAN-OS Management Interface',
      affected_versions: '10.2.x < 10.2.12-h2, 11.0.x < 11.0.6-h1, 11.1.x < 11.1.5-h1, 11.2.x < 11.2.4-h1',
      attack_vector: 'Network',
      attack_complexity: 'Low',
      privileges_required: 'None',
      user_interaction: 'None',
      patch_available: true,
      patch_version: '10.2.12-h2, 11.0.6-h1, 11.1.5-h1, 11.2.4-h1',
      workaround: 'Restrict management interface to trusted IPs only.',
      exploit_available: true,
      actively_exploited: true,
      references: [
        { title: 'Palo Alto Security Advisory', url: 'https://security.paloaltonetworks.com/CVE-2024-0012' },
        { title: 'NVD Entry', url: 'https://nvd.nist.gov/vuln/detail/CVE-2024-0012' }
      ]
    }
  };
  
  return details[cve_id] || {
    vendor: 'Consultar NVD',
    product: 'N/A',
    affected_versions: 'Ver advisory',
    attack_vector: 'Network',
    attack_complexity: 'Unknown',
    privileges_required: 'Unknown',
    user_interaction: 'Unknown',
    patch_available: null,
    patch_version: 'Consultar vendor',
    workaround: 'Consultar advisory do fabricante.',
    exploit_available: false,
    actively_exploited: false,
    references: [{ title: 'NVD Entry', url: `https://nvd.nist.gov/vuln/detail/${cve_id}` }]
  };
};

const getRemediationSteps = () => [
  { step: 1, title: 'Identificar sistemas afetados', description: 'Execute um scan de vulnerabilidades para mapear todos os ativos com esta CVE.', command: 'nmap --script vuln TARGET_IP' },
  { step: 2, title: 'Avaliar criticidade', description: 'Determine o impacto no negócio dos sistemas afetados para priorização.', command: null },
  { step: 3, title: 'Aplicar mitigação temporária', description: 'Se não puder aplicar patch imediatamente, aplique workaround.', command: null },
  { step: 4, title: 'Aplicar patch', description: 'Agende janela de manutenção e aplique o patch do fabricante.', command: 'apt update && apt upgrade -y' },
  { step: 5, title: 'Validar correção', description: 'Execute novo scan para confirmar que a vulnerabilidade foi corrigida.', command: 'nmap --script vuln TARGET_IP' },
  { step: 6, title: 'Documentar', description: 'Registre todas as ações para compliance e auditoria.', command: null },
];

export function VulnerabilitiesPage() {
  const [vulnerabilities, setVulnerabilities] = useState([]);
  const [filteredVulns, setFilteredVulns] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [syncingNVD, setSyncingNVD] = useState(false);
  const [exportingPDF, setExportingPDF] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [severityFilter, setSeverityFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  const [selectedVuln, setSelectedVuln] = useState(null);

  useEffect(() => { loadVulnerabilities(); }, []);
  useEffect(() => { filterVulnerabilities(); }, [vulnerabilities, searchTerm, severityFilter, statusFilter]);

  const loadVulnerabilities = async () => {
    try {
      const response = await vulnerabilitiesAPI.getAll();
      setVulnerabilities(response.data.vulnerabilities);
    } catch (error) {
      console.error('Error loading vulnerabilities:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleNVDSync = async () => {
    setSyncingNVD(true);
    try {
      await nvdAPI.sync();
      toast.success('Sincronização NVD + CISA KEV iniciada! Aguarde alguns segundos...');
      setTimeout(() => { loadVulnerabilities(); setSyncingNVD(false); }, 5000);
    } catch {
      toast.error('Erro ao sincronizar NVD');
      setSyncingNVD(false);
    }
  };

  const handleExportPDF = async () => {
    setExportingPDF(true);
    try {
      const res = await reportsAPI.getVulnPDF(severityFilter !== 'all' ? severityFilter : undefined);
      const url = URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
      const a = document.createElement('a');
      a.href = url;
      a.download = `vulnerabilidades_${new Date().toISOString().substring(0,10)}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success('PDF exportado com sucesso!');
    } catch {
      toast.error('Erro ao exportar PDF');
    } finally {
      setExportingPDF(false);
    }
  };

  const filterVulnerabilities = () => {
    let filtered = [...vulnerabilities];
    
    if (searchTerm) {
      filtered = filtered.filter(v =>
        v.cve_id.toLowerCase().includes(searchTerm.toLowerCase()) ||
        v.title.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }
    
    if (severityFilter !== 'all') {
      filtered = filtered.filter(v => v.severity === severityFilter);
    }
    
    if (statusFilter !== 'all') {
      filtered = filtered.filter(v => v.status === statusFilter);
    }
    
    setFilteredVulns(filtered);
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
  };

  const stats = {
    critical: vulnerabilities.filter(v => v.severity === 'critical').length,
    high: vulnerabilities.filter(v => v.severity === 'high').length,
    medium: vulnerabilities.filter(v => v.severity === 'medium').length,
    low: vulnerabilities.filter(v => v.severity === 'low').length,
  };

  const getCVSSColor = (score) => {
    if (score >= 9.0) return 'text-destructive';
    if (score >= 7.0) return 'text-orange-500';
    if (score >= 4.0) return 'text-warning';
    return 'text-info';
  };

  return (
    <div className="min-h-screen" data-testid="vulnerabilities-page">
      <Header title="Vulnerabilities" />
      
      <div className="p-6 space-y-6">
        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <Card className="bg-destructive/10 border-destructive/30 cursor-pointer hover:bg-destructive/20" onClick={() => setSeverityFilter('critical')}>
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs text-muted-foreground uppercase">Critical</p>
                  <p className="text-3xl font-mono font-bold text-destructive">{stats.critical}</p>
                </div>
                <Bug className="h-8 w-8 text-destructive opacity-50" />
              </div>
            </CardContent>
          </Card>
          <Card className="bg-orange-500/10 border-orange-500/30 cursor-pointer hover:bg-orange-500/20" onClick={() => setSeverityFilter('high')}>
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs text-muted-foreground uppercase">High</p>
                  <p className="text-3xl font-mono font-bold text-orange-500">{stats.high}</p>
                </div>
                <AlertTriangle className="h-8 w-8 text-orange-500 opacity-50" />
              </div>
            </CardContent>
          </Card>
          <Card className="bg-warning/10 border-warning/30 cursor-pointer hover:bg-warning/20" onClick={() => setSeverityFilter('medium')}>
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs text-muted-foreground uppercase">Medium</p>
                  <p className="text-3xl font-mono font-bold text-warning">{stats.medium}</p>
                </div>
                <Shield className="h-8 w-8 text-warning opacity-50" />
              </div>
            </CardContent>
          </Card>
          <Card className="bg-info/10 border-info/30 cursor-pointer hover:bg-info/20" onClick={() => setSeverityFilter('low')}>
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs text-muted-foreground uppercase">Low</p>
                  <p className="text-3xl font-mono font-bold text-info">{stats.low}</p>
                </div>
                <Shield className="h-8 w-8 text-info opacity-50" />
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Filters */}
        <Card className="bg-card/50 border-border">
          <CardContent className="pt-6">
            <div className="flex flex-wrap gap-4">
              <div className="relative flex-1 min-w-[200px]">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder="Buscar CVE ou título..."
                  className="pl-9"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  data-testid="vuln-search"
                />
              </div>
              <Select value={severityFilter} onValueChange={setSeverityFilter}>
                <SelectTrigger className="w-[150px]">
                  <SelectValue placeholder="Severidade" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todas</SelectItem>
                  <SelectItem value="critical">Critical</SelectItem>
                  <SelectItem value="high">High</SelectItem>
                  <SelectItem value="medium">Medium</SelectItem>
                  <SelectItem value="low">Low</SelectItem>
                </SelectContent>
              </Select>
              <Select value={statusFilter} onValueChange={setStatusFilter}>
                <SelectTrigger className="w-[150px]">
                  <SelectValue placeholder="Status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todos</SelectItem>
                  <SelectItem value="open">Open</SelectItem>
                  <SelectItem value="patched">Patched</SelectItem>
                  <SelectItem value="mitigated">Mitigated</SelectItem>
                  <SelectItem value="accepted">Risk Accepted</SelectItem>
                </SelectContent>
              </Select>
              <Button variant="outline" onClick={loadVulnerabilities}>
                <RefreshCw className="h-4 w-4 mr-2" />
                Atualizar
              </Button>
              <Button
                variant="outline"
                onClick={handleNVDSync}
                disabled={syncingNVD}
                data-testid="nvd-sync-btn"
              >
                {syncingNVD ? <RefreshCw className="h-4 w-4 mr-2 animate-spin" /> : <CloudDownload className="h-4 w-4 mr-2" />}
                {syncingNVD ? 'Sincronizando...' : 'Sync NVD'}
              </Button>
              <Button
                onClick={handleExportPDF}
                disabled={exportingPDF}
                data-testid="export-vuln-pdf-btn"
              >
                {exportingPDF ? <RefreshCw className="h-4 w-4 mr-2 animate-spin" /> : <Download className="h-4 w-4 mr-2" />}
                Exportar PDF
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Table */}
        <Card className="bg-card/50 border-border">
          <CardHeader>
            <CardTitle className="font-display text-sm uppercase tracking-wider text-muted-foreground">
              Vulnerabilidades Detectadas ({filteredVulns.length})
            </CardTitle>
          </CardHeader>
          <CardContent>
            <ScrollArea className="h-[500px]">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>CVE ID</TableHead>
                    <TableHead>Título</TableHead>
                    <TableHead>Severidade</TableHead>
                    <TableHead>CVSS</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Exploit</TableHead>
                    <TableHead></TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredVulns.map((vuln) => {
                    const details = getCVEDetails(vuln.cve_id);
                    return (
                      <TableRow 
                        key={vuln.id} 
                        className="hover:bg-secondary/50 cursor-pointer"
                        onClick={() => setSelectedVuln(vuln)}
                        data-testid={`vuln-row-${vuln.id}`}
                      >
                        <TableCell>
                          <span className="font-mono text-info">{vuln.cve_id}</span>
                        </TableCell>
                        <TableCell>
                          <div>
                            <p className="font-medium">{vuln.title}</p>
                            <p className="text-xs text-muted-foreground">{details.vendor} - {details.product}</p>
                          </div>
                        </TableCell>
                        <TableCell>
                          <Badge className={cn("border", getSeverityColor(vuln.severity))}>
                            {vuln.severity.toUpperCase()}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          <span className={cn("font-mono font-bold text-lg", getCVSSColor(vuln.cvss_score))}>
                            {vuln.cvss_score}
                          </span>
                        </TableCell>
                        <TableCell>
                          <Badge variant={vuln.status === 'patched' ? 'default' : 'secondary'}>
                            {vuln.status}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          {details.actively_exploited ? (
                            <Badge variant="destructive" className="animate-pulse">
                              EXPLOITED
                            </Badge>
                          ) : details.exploit_available ? (
                            <Badge variant="outline" className="text-warning border-warning">
                              POC
                            </Badge>
                          ) : (
                            <span className="text-muted-foreground text-sm">-</span>
                          )}
                        </TableCell>
                        <TableCell>
                          <Button variant="ghost" size="sm">
                            Ver Detalhes
                          </Button>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </ScrollArea>
          </CardContent>
        </Card>
      </div>

      {/* Vulnerability Detail Modal */}
      <Dialog open={!!selectedVuln} onOpenChange={() => setSelectedVuln(null)}>
        <DialogContent className="max-w-5xl max-h-[90vh] overflow-hidden">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-3">
              <Bug className="h-6 w-6 text-warning" />
              <span className="font-mono">{selectedVuln?.cve_id}</span>
              <Badge className={cn("border", getSeverityColor(selectedVuln?.severity))}>
                CVSS {selectedVuln?.cvss_score}
              </Badge>
              {getCVEDetails(selectedVuln?.cve_id || '').actively_exploited && (
                <Badge variant="destructive" className="animate-pulse">ACTIVELY EXPLOITED</Badge>
              )}
            </DialogTitle>
          </DialogHeader>
          
          {selectedVuln && (
            <Tabs defaultValue="overview" className="mt-4">
              <TabsList className="bg-secondary">
                <TabsTrigger value="overview">Overview</TabsTrigger>
                <TabsTrigger value="technical">Técnico</TabsTrigger>
                <TabsTrigger value="remediation">Remediação</TabsTrigger>
                <TabsTrigger value="affected">Assets Afetados</TabsTrigger>
                <TabsTrigger value="references">Referências</TabsTrigger>
              </TabsList>
              
              <ScrollArea className="h-[500px] mt-4">
                <TabsContent value="overview" className="space-y-4 mt-0">
                  <Card className="bg-secondary/50">
                    <CardContent className="pt-4">
                      <h4 className="font-display text-xs uppercase text-muted-foreground mb-2">Descrição</h4>
                      <p className="text-sm">{selectedVuln.description}</p>
                    </CardContent>
                  </Card>

                  <div className="grid grid-cols-2 gap-4">
                    <Card className="bg-secondary/50">
                      <CardContent className="pt-4">
                        <h4 className="font-display text-xs uppercase text-muted-foreground mb-3">Informações da CVE</h4>
                        <div className="space-y-2">
                          <div className="flex justify-between">
                            <span className="text-sm text-muted-foreground">CVE ID</span>
                            <div className="flex items-center gap-1">
                              <span className="font-mono text-info">{selectedVuln.cve_id}</span>
                              <Button size="icon" variant="ghost" className="h-6 w-6" onClick={() => copyToClipboard(selectedVuln.cve_id)}>
                                <Copy className="h-3 w-3" />
                              </Button>
                            </div>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-sm text-muted-foreground">CVSS Score</span>
                            <span className={cn("font-mono font-bold", getCVSSColor(selectedVuln.cvss_score))}>
                              {selectedVuln.cvss_score} / 10.0
                            </span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-sm text-muted-foreground">Severidade</span>
                            <Badge className={cn("border", getSeverityColor(selectedVuln.severity))}>
                              {selectedVuln.severity.toUpperCase()}
                            </Badge>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-sm text-muted-foreground">Status</span>
                            <Badge variant={selectedVuln.status === 'patched' ? 'default' : 'destructive'}>
                              {selectedVuln.status.toUpperCase()}
                            </Badge>
                          </div>
                        </div>
                      </CardContent>
                    </Card>

                    <Card className="bg-secondary/50">
                      <CardContent className="pt-4">
                        <h4 className="font-display text-xs uppercase text-muted-foreground mb-3">Produto Afetado</h4>
                        <div className="space-y-2">
                          <div className="flex justify-between">
                            <span className="text-sm text-muted-foreground">Vendor</span>
                            <span className="font-mono text-sm">{getCVEDetails(selectedVuln.cve_id).vendor}</span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-sm text-muted-foreground">Produto</span>
                            <span className="font-mono text-sm">{getCVEDetails(selectedVuln.cve_id).product}</span>
                          </div>
                          <div>
                            <span className="text-sm text-muted-foreground">Versões Afetadas</span>
                            <p className="font-mono text-xs mt-1 bg-background p-2 rounded">
                              {getCVEDetails(selectedVuln.cve_id).affected_versions}
                            </p>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  </div>

                  {getCVEDetails(selectedVuln.cve_id).patch_available && (
                    <Card className="bg-success/10 border-success/30">
                      <CardContent className="pt-4">
                        <h4 className="font-display text-xs uppercase text-success mb-2 flex items-center gap-2">
                          <CheckCircle className="h-4 w-4" /> Patch Disponível
                        </h4>
                        <p className="text-sm">
                          Atualize para: <span className="font-mono font-bold">{getCVEDetails(selectedVuln.cve_id).patch_version}</span>
                        </p>
                      </CardContent>
                    </Card>
                  )}

                  {getCVEDetails(selectedVuln.cve_id).actively_exploited && (
                    <Card className="bg-destructive/10 border-destructive/30">
                      <CardContent className="pt-4">
                        <h4 className="font-display text-xs uppercase text-destructive mb-2 flex items-center gap-2">
                          <AlertTriangle className="h-4 w-4" /> Exploração Ativa Detectada
                        </h4>
                        <p className="text-sm">
                          Esta vulnerabilidade está sendo ativamente explorada. Priorize a remediação imediatamente.
                        </p>
                      </CardContent>
                    </Card>
                  )}
                </TabsContent>

                <TabsContent value="technical" className="space-y-4 mt-0">
                  <Card className="bg-secondary/50">
                    <CardContent className="pt-4">
                      <h4 className="font-display text-xs uppercase text-muted-foreground mb-3">CVSS v3.1 Metrics</h4>
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <span className="text-xs text-muted-foreground">Attack Vector</span>
                          <p className="font-mono mt-1">{getCVEDetails(selectedVuln.cve_id).attack_vector}</p>
                        </div>
                        <div>
                          <span className="text-xs text-muted-foreground">Attack Complexity</span>
                          <p className="font-mono mt-1">{getCVEDetails(selectedVuln.cve_id).attack_complexity}</p>
                        </div>
                        <div>
                          <span className="text-xs text-muted-foreground">Privileges Required</span>
                          <p className="font-mono mt-1">{getCVEDetails(selectedVuln.cve_id).privileges_required}</p>
                        </div>
                        <div>
                          <span className="text-xs text-muted-foreground">User Interaction</span>
                          <p className="font-mono mt-1">{getCVEDetails(selectedVuln.cve_id).user_interaction}</p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>

                  <Card className="bg-warning/10 border-warning/30">
                    <CardContent className="pt-4">
                      <h4 className="font-display text-xs uppercase text-warning mb-2 flex items-center gap-2">
                        <Shield className="h-4 w-4" /> Workaround / Mitigação
                      </h4>
                      <p className="text-sm">{getCVEDetails(selectedVuln.cve_id).workaround}</p>
                    </CardContent>
                  </Card>

                  <Card className="bg-secondary/50">
                    <CardContent className="pt-4">
                      <h4 className="font-display text-xs uppercase text-muted-foreground mb-3">CVSS Score Breakdown</h4>
                      <div className="space-y-3">
                        <div>
                          <div className="flex justify-between text-sm mb-1">
                            <span>Base Score</span>
                            <span className={cn("font-mono font-bold", getCVSSColor(selectedVuln.cvss_score))}>
                              {selectedVuln.cvss_score}
                            </span>
                          </div>
                          <Progress value={selectedVuln.cvss_score * 10} className="h-2" />
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                </TabsContent>

                <TabsContent value="remediation" className="space-y-4 mt-0">
                  <Card className="bg-primary/10 border-primary/30">
                    <CardContent className="pt-4">
                      <h4 className="font-display text-xs uppercase text-primary mb-2 flex items-center gap-2">
                        <Lightbulb className="h-4 w-4" /> Plano de Remediação
                      </h4>
                      <p className="text-sm">
                        Siga os passos abaixo para remediar esta vulnerabilidade de forma segura e documentada.
                      </p>
                    </CardContent>
                  </Card>

                  {getRemediationSteps().map((step) => (
                    <Card key={step.step} className="bg-secondary/50">
                      <CardContent className="pt-4">
                        <div className="flex items-start gap-4">
                          <div className="w-8 h-8 rounded-full bg-primary/20 flex items-center justify-center text-primary font-bold flex-shrink-0">
                            {step.step}
                          </div>
                          <div className="flex-1">
                            <h5 className="font-medium">{step.title}</h5>
                            <p className="text-sm text-muted-foreground mt-1">{step.description}</p>
                            {step.command && (
                              <div className="mt-2 flex items-center gap-2">
                                <code className="flex-1 text-xs bg-background p-2 rounded font-mono overflow-x-auto">
                                  {step.command}
                                </code>
                                <Button size="icon" variant="ghost" onClick={() => copyToClipboard(step.command)}>
                                  <Copy className="h-4 w-4" />
                                </Button>
                              </div>
                            )}
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  ))}

                  <div className="flex gap-2 pt-4">
                    <Button className="flex-1">
                      <Target className="h-4 w-4 mr-2" />
                      Criar Ticket de Remediação
                    </Button>
                    <Button variant="outline" onClick={handleExportPDF} disabled={exportingPDF}>
                      <FileText className="h-4 w-4 mr-2" />
                      Exportar PDF
                    </Button>
                  </div>
                </TabsContent>

                <TabsContent value="affected" className="space-y-4 mt-0">
                  <Card className="bg-secondary/50">
                    <CardContent className="pt-4">
                      <h4 className="font-display text-xs uppercase text-muted-foreground mb-3 flex items-center gap-2">
                        <Server className="h-4 w-4" /> Assets Afetados
                      </h4>
                      {selectedVuln.affected_assets?.length > 0 ? (
                        <div className="space-y-2">
                          {selectedVuln.affected_assets.map((asset, i) => (
                            <div key={i} className="flex items-center justify-between p-3 bg-background rounded">
                              <div className="flex items-center gap-3">
                                <Server className="h-5 w-5 text-muted-foreground" />
                                <div>
                                  <span className="font-mono">{asset}</span>
                                  <p className="text-xs text-muted-foreground">Vulnerável</p>
                                </div>
                              </div>
                              <Badge variant="destructive">AFFECTED</Badge>
                            </div>
                          ))}
                        </div>
                      ) : (
                        <div className="text-center py-8">
                          <Target className="h-12 w-12 mx-auto mb-4 text-muted-foreground" />
                          <p className="text-muted-foreground">Nenhum asset específico mapeado</p>
                          <p className="text-xs text-muted-foreground mt-1">Execute um scan para identificar assets afetados</p>
                          <Button className="mt-4">
                            <Search className="h-4 w-4 mr-2" />
                            Executar Vulnerability Scan
                          </Button>
                        </div>
                      )}
                    </CardContent>
                  </Card>
                </TabsContent>

                <TabsContent value="references" className="space-y-4 mt-0">
                  <Card className="bg-secondary/50">
                    <CardContent className="pt-4">
                      <h4 className="font-display text-xs uppercase text-muted-foreground mb-3">Links de Referência</h4>
                      <div className="space-y-2">
                        {getCVEDetails(selectedVuln.cve_id).references.map((ref, i) => (
                          <a
                            key={i}
                            href={ref.url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="flex items-center gap-3 p-3 bg-background rounded hover:bg-primary/10 transition-colors"
                          >
                            <ExternalLink className="h-4 w-4 text-info flex-shrink-0" />
                            <div>
                              <span className="text-sm font-medium">{ref.title}</span>
                              <p className="text-xs text-muted-foreground truncate">{ref.url}</p>
                            </div>
                          </a>
                        ))}
                      </div>
                    </CardContent>
                  </Card>

                  <Card className="bg-secondary/50">
                    <CardContent className="pt-4">
                      <h4 className="font-display text-xs uppercase text-muted-foreground mb-3">Copiar Informações</h4>
                      <div className="space-y-2">
                        <div className="flex items-center gap-2">
                          <span className="text-sm text-muted-foreground w-20">CVE ID:</span>
                          <code className="flex-1 bg-background p-2 rounded font-mono text-sm">{selectedVuln.cve_id}</code>
                          <Button size="icon" variant="ghost" onClick={() => copyToClipboard(selectedVuln.cve_id)}>
                            <Copy className="h-4 w-4" />
                          </Button>
                        </div>
                        <div className="flex items-center gap-2">
                          <span className="text-sm text-muted-foreground w-20">NVD Link:</span>
                          <code className="flex-1 bg-background p-2 rounded font-mono text-xs overflow-hidden">
                            https://nvd.nist.gov/vuln/detail/{selectedVuln.cve_id}
                          </code>
                          <Button size="icon" variant="ghost" onClick={() => copyToClipboard(`https://nvd.nist.gov/vuln/detail/${selectedVuln.cve_id}`)}>
                            <Copy className="h-4 w-4" />
                          </Button>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                </TabsContent>
              </ScrollArea>
            </Tabs>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

export default VulnerabilitiesPage;
